// Claiming an unclaimed instance. spec/04 §8c.
//
// The one screen that creates an account without an account already existing,
// so it is deliberately plain: an instance is either unclaimed or it is not,
// and there is nothing here to explore.
//
// It asks for a setup token only when the server says one is needed
// (`requiresSetupToken`). An instance that opened itself deliberately would
// otherwise show a field nobody can fill, and an operator would reasonably
// conclude they were locked out of their own deployment.

import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { ApiError, claimInstance, setupStatus, type SetupStatus } from './api.ts';

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; status: SetupStatus }
  | { kind: 'claimed' }
  | { kind: 'done'; email: string; organisationName: string }
  | { kind: 'unreachable'; message: string };

export function SetupPage(): ReactElement {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setupStatus()
      .then((status) => {
        if (!live) return;
        setPhase(status.claimed ? { kind: 'claimed' } : { kind: 'ready', status });
      })
      .catch((error: unknown) => {
        if (!live) return;
        setPhase({
          kind: 'unreachable',
          message: error instanceof Error ? error.message : 'The API did not answer.',
        });
      });
    return () => {
      live = false;
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFailure(null);
    setSubmitting(true);

    const form = new FormData(event.currentTarget);
    const read = (field: string) => String(form.get(field) ?? '').trim();
    const password = String(form.get('password') ?? '');
    const email = read('email');
    const organisationName = read('organisationName');
    const name = read('name');
    const setupToken = read('setupToken');

    // Checked here as a courtesy to the person typing, and again on the
    // server, which is the one that decides (AD-01). A mismatch caught after a
    // round trip is a password nobody can reproduce.
    if (password !== String(form.get('passwordConfirm') ?? '')) {
      setFailure('Those two passwords are not the same.');
      setSubmitting(false);
      return;
    }

    try {
      await claimInstance({
        email,
        password,
        organisationName,
        ...(name === '' ? {} : { name }),
        ...(setupToken === '' ? {} : { setupToken }),
      });
      setPhase({ kind: 'done', email, organisationName });
    } catch (error) {
      // The server's message, not one invented here: it is the thing that
      // knows whether the token was wrong, the password was refused, or
      // somebody else claimed the instance a second earlier.
      setFailure(
        error instanceof ApiError
          ? error.message
          : 'Something between this page and the API answered instead.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (phase.kind === 'loading') {
    return (
      <main className="wrap">
        <h1>signrs</h1>
        <p className="muted">Checking whether this instance has been set up…</p>
      </main>
    );
  }

  if (phase.kind === 'unreachable') {
    return (
      <main className="wrap">
        <h1>signrs</h1>
        <div className="error">
          <p>This page could not reach the API.</p>
          <p className="muted">{phase.message}</p>
        </div>
      </main>
    );
  }

  if (phase.kind === 'claimed') {
    return (
      <main className="wrap">
        <h1>signrs</h1>
        <p>This instance already has an administrator.</p>
        <p className="muted">
          Setting it up again is not possible — that is the guarantee that stops a
          stranger taking over a running deployment.
        </p>
      </main>
    );
  }

  if (phase.kind === 'done') {
    return (
      <main className="wrap">
        <h1>signrs</h1>
        <div className="notice">
          <p>
            <strong>{phase.organisationName}</strong> is set up, and{' '}
            <strong>{phase.email}</strong> is its owner.
          </p>
          <p className="muted">
            This screen cannot be used again. There is no sign-in page yet, so the
            API is the way in from here.
          </p>
        </div>
      </main>
    );
  }

  const { status } = phase;

  return (
    <main className="wrap">
      <h1>Set up signrs</h1>
      <p className="muted">
        Nobody has claimed this instance yet. Whoever completes this form becomes its
        first administrator.
      </p>

      {!status.requiresSetupToken && (
        <div className="notice">
          <p>
            <strong>This instance accepts a setup without a token.</strong> Anyone who
            reaches this page before you finish can claim it instead. That is a
            deliberate setting for a development instance (<code>SETUP_OPEN_CLAIM</code>)
            and is not how a deployment holding signatures should run.
          </p>
        </div>
      )}

      <form onSubmit={submit} className="assent">
        {status.requiresSetupToken && (
          <p>
            <label htmlFor="setupToken">Setup token</label>
            <input
              id="setupToken"
              name="setupToken"
              type="password"
              autoComplete="off"
              required
            />
            <span className="muted">
              Written to <code>/etc/esign/env</code> when the server was provisioned.
            </span>
          </p>
        )}

        <p>
          <label htmlFor="organisationName">Organisation name</label>
          <input id="organisationName" name="organisationName" type="text" required />
        </p>

        <p>
          <label htmlFor="email">Your email address</label>
          <input id="email" name="email" type="email" autoComplete="email" required />
        </p>

        <p>
          <label htmlFor="name">Your name (optional)</label>
          <input id="name" name="name" type="text" autoComplete="name" />
        </p>

        <p>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
          />
        </p>

        <p>
          <label htmlFor="passwordConfirm">Password again</label>
          <input
            id="passwordConfirm"
            name="passwordConfirm"
            type="password"
            autoComplete="new-password"
            required
          />
        </p>

        {failure !== null && (
          <div className="error" role="alert">
            <p>{failure}</p>
          </div>
        )}

        <div className="actions">
          <button type="submit" disabled={submitting}>
            {submitting ? 'Setting up…' : 'Set up this instance'}
          </button>
        </div>
      </form>
    </main>
  );
}
